import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import moment from "moment";
import { inventoryActions } from "../../../redux/actions";
import Topbar from "../../../shared/components/topbar/Topbar";
import ButtonWithToolTip from "../../../shared/components/Buttons/ButtonWithToolTip";
import DataTableComponent from "../../../shared/components/table/ReactDataTableComponent";
import DashboardDatePicker from "../../../shared/components/datetimePickerDropdown/DashboardDatePicker";
import ConfirmationModalWithValue from "../../../views/Inventory/Items/ConfirmationModal/ConfirmationModalWithValue";
import {
  toastMessages,
  inventoryConstants,
  buttonNameConstants,
  routesPathConstants,
  AUDIT_SESSIONS_DATEPICKER_OPTIONS,
  datepickerConstants,
  defaultGridStateDataTable,
  TABLE_IDS,
} from "../../../constants";
import {
  getStockAuditColumns,
  getAuditExpandedRow,
  convertDataToDisplayable,
} from "../../../shared/utilities/columnHeadersUtility/stockAudit";
import {
  getTimeFrame,
  isSpinnerEnabled,
  createCompanyDynamicUrl,
} from "../../../shared/utility";

const StockAudit = (props) => {
  const {
    user,
    stockAudit,
    currentStore,
    spinnerArray,
    getPaginatedStockAudit,
    startNewAuditSession,
  } = props;

  const navigate = useNavigate();
  let numOfRenders = useRef(0);
  const [isConfirmationModal, setIsConfirmationModal] = useState(false);
  const [timeFrame, setTimeFrame] = useState({
    start: moment().startOf(datepickerConstants.YEAR),
    end: moment().endOf(datepickerConstants.YEAR),
    duration: "yearly",
  });
  const [isActiveAuditSession, setIsActiveAuditSession] = useState(false);
  const [gridState, setGridState] = useState(defaultGridStateDataTable);
  const [sortFilter, setSortFilter] = useState("startDate DESC ");
  const [expandedRowId, setExpandedRowId] = useState(null);

  const gridData = useMemo(() => {
    return convertDataToDisplayable(stockAudit?.auditData) || [];
  }, [stockAudit]);

  useEffect(() => {
    setIsActiveAuditSession(stockAudit?.isActiveAuditSession || false);
  }, [stockAudit]);

  useEffect(() => {
    if (timeFrame.start && timeFrame.end) {
      numOfRenders.current++;
      getPaginatedStockAudit(
        currentStore?.id,
        { ...gridState, sortFilter: sortFilter },
        {
          ...timeFrame,
        }
      );
    }
  }, [
    gridState,
    timeFrame,
    sortFilter,
    getPaginatedStockAudit,
    currentStore?.id,
  ]);

  const toggleConfirmationModal = () => {
    setIsConfirmationModal(!isConfirmationModal);
  };

  const handleConfirmNoButtonClick = () => {
    toggleConfirmationModal();
  };

  const getRequestAfterCreatingAudit = () => {
    if (timeFrame.start && timeFrame.end) {
      numOfRenders.current++;
      getPaginatedStockAudit(
        currentStore?.id,
        { ...gridState, sortFilter: sortFilter },
        {
          ...timeFrame,
        }
      );
    }
  };

  const handleConfirmYesButtonClick = (value) => {
    startNewAuditSession(
      currentStore?.id,
      {
        store: { id: currentStore?.id, name: currentStore?.storeName },
        nickName: value,
        startedBy: `${user.firstName} ${user.lastName}`,
        status: buttonNameConstants.IN_PROGRESS,
      },
      navigate //getRequestAfterCreatingAudit
    );
    toggleConfirmationModal();
  };

  const handleAddSession = () => {
    toggleConfirmationModal();
  };

  const handleViewClick = (row) => {
    navigate(
      createCompanyDynamicUrl(
        routesPathConstants.INVENTORY_ITEMS_STOCK_AUDIT_BREAKDOWN
      ),
      {
        state: {
          currentAudit: row,
        },
      }
    );
  };
  const handleContinueClick = (row) => {
    navigate(
      createCompanyDynamicUrl(
        routesPathConstants.INVENTORY_ITEMS_STOCK_AUDIT_SESSION
      ),
      {
        state: {
          currentAudit: row.unconvertedData,
        },
      }
    );
  };
  const [auditColumns, setAuditColumns] = useState(
    getStockAuditColumns(handleViewClick, handleContinueClick)
  );

  const handleGetTimeFrame = (value) => {
    getTimeFrame(value, timeFrame, setTimeFrame);
  };

  const handleRowClick = (row) => {
    // If the clicked row is already expanded, collapse it
    if (expandedRowId === row.id) {
      setExpandedRowId(null);
    } else {
      setExpandedRowId(row.id);
    }
  };

  const onTableSort = (column, sortDirection) => {
    setSortFilter(`${column.id} ${sortDirection.toUpperCase()} `);
    setAuditColumns(
      auditColumns.map((col) => {
        if (col.id === column.id) {
          return {
            ...col,
            sortDirection,
          };
        } else {
          return {
            ...col,
            sortDirection: null,
          };
        }
      })
    );
  };

  //need to pass these because database call for pagination is called if they are passed
  const handlePageChange = () => {};
  const handlePerRowsChange = () => {};

  const skeletonArray = Array(10).fill({});
  return (
    <>
      <Topbar title="Audit Sessions">
        <ButtonWithToolTip
          handleClick={handleAddSession}
          isDisabled={isActiveAuditSession}
          label={buttonNameConstants.NEW_AUDIT_SESSION}
          toolTipBody={toastMessages.ACTIVE_AUDIT_SESSION_PRESENT}
        />
      </Topbar>
      <Row className="m-0">
        <Col md={12} className=" d-flex gap-2 align-items-center">
          <DashboardDatePicker
            label="Date"
            datePickerOptions={AUDIT_SESSIONS_DATEPICKER_OPTIONS}
            enableOptionsSelection={false}
            getTimeFrame={handleGetTimeFrame}
            initialValue={timeFrame}
            disableTimeframeDateFormat={true}
          />
        </Col>
      </Row>
      <Row className="m-0 mt-3 w-100 h-100 user-screen-wrapper pt-3">
        <DataTableComponent
          tableId={TABLE_IDS.STOCK_AUDIT_LIST}
          key={numOfRenders.current}
          columns={auditColumns}
          data={
            isSpinnerEnabled(spinnerArray, [inventoryConstants.GET_STOCK_AUDIT])
              ? skeletonArray
              : gridData
          }
          keyField="id"
          expandableRows={true}
          expandableRowsHideExpander={true}
          expandableRowsComponent={getAuditExpandedRow}
          pointerOnHover={true}
          highlightOnHover={true}
          onRowClicked={handleRowClick}
          expandedRowId={expandedRowId}
          paginationServer={true}
          total={stockAudit?.totalRecords || 0}
          gridState={gridState}
          setGridState={setGridState}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          onSort={onTableSort}
          className="audit-table"
        />
      </Row>
      <ConfirmationModalWithValue
        isConfirmationModal={isConfirmationModal}
        toggleConfirmationModal={toggleConfirmationModal}
        handleNoButtonClick={handleConfirmNoButtonClick}
        handleYesButtonClick={handleConfirmYesButtonClick}
        title="New Audit Session"
        showInput={true}
        inputLabel={buttonNameConstants.NICK_NAME}
        inputPlaceholder="Enter nickname"
        noButtonText={buttonNameConstants.CANCEL}
        yesButtonText={buttonNameConstants.START_AUDIT}
        isLoading={isSpinnerEnabled(spinnerArray, [
          inventoryConstants.START_NEW_AUDIT_SESSION,
        ])}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.authentication.user,
  spinnerArray: state.system.spinnerArray,
  stockAudit: state.inventory.stockAudit,
  currentStore: state.store.currentStore,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getPaginatedStockAudit: (storeId, gridState, timeFrame) =>
    dispatch(
      inventoryActions.getPaginatedStockAudit(storeId, gridState, timeFrame)
    ),
  startNewAuditSession: (storeId, data, navigate) =>
    dispatch(inventoryActions.startNewAuditSession(storeId, data, navigate)),
});

//-------Export AddInvoice Component
export default connect(mapStateToProps, mapDispatchToProps)(StockAudit);
